import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dag0ikrep053ijub216g";

type Feedback = { type: "success" | "error"; text: string } | null;

export default function SubmitForm() {
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [sending, setSending] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") ?? "").trim();
    if (honeypot) {
      setFeedback({ type: "success", text: "投稿已提交，感谢你的支持，我们会尽快处理。" });
      form.reset();
      return;
    }
    if (honeypot) return;

    setSending(true);
    setFeedback(null);
    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => {
        if (value && String(value).trim()) body.append(key, String(value));
      });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        "";
      if (res.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg)) {
        setFeedback({
          type: "success",
          text: "投稿已提交，感谢你的支持，我们会尽快核实处理。",
        });
        form.reset();
      } else {
        setFeedback({
          type: "error",
          text: serverMsg || "投稿提交失败，请稍后重试。",
        });
      }
    } catch {
      setFeedback({ type: "error", text: "网络异常，投稿未能提交，请稍后重试。" });
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="submit" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-5xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 overflow-hidden rounded-3xl border border-background-200 bg-background-50 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative bg-background-100 p-6 md:p-8">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20mail%20slot%20and%20megaphone%20with%20warm%20red%20and%20amber%20light%2C%20anonymous%20envelope%20and%20speech%20bubble%20motifs%20floating%2C%20dark%20charcoal%20background%20with%20paper%20fragments%2C%20magazine%20collage%20aesthetic%2C%20no%20real%20people%20or%20readable%20text%2C%20moody%20cinematic%20high-detail%20artistic%20composition&width=800&height=900&seq=melon-submit&orientation=portrait"
              alt="匿名投稿通道"
              className="hidden h-full w-full rounded-2xl object-cover lg:block"
            />
            <div className="relative z-10 mt-0 lg:absolute lg:inset-x-0 lg:bottom-0 lg:p-8 lg:pt-24 lg:bg-gradient-to-t lg:from-foreground-950/85 lg:to-transparent">
              <p className="text-xs font-semibold tracking-[0.3em] text-primary-700 lg:text-accent-300">
                ANONYMOUS TIP
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 lg:text-foreground-50 md:text-3xl">
                匿名投稿通道
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700 lg:text-background-100/90">
                有内幕想分享？直接投过来。无需实名，默认匿名处理。请勿包含他人隐私，
                编造、造谣或带有人身攻击的内容会被拒稿。
              </p>
            </div>
          </div>

          <div className="p-6 md:p-8">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-foreground-800">
                  爆料类型
                  <select
                    name="category"
                    required
                    className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="明星娱乐">明星娱乐</option>
                    <option value="影视剧综">影视剧综</option>
                    <option value="综艺饭圈">综艺饭圈</option>
                    <option value="体育电竞">体育电竞</option>
                    <option value="职场圈">职场圈</option>
                    <option value="纠错/申诉">纠错 / 申诉</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 text-sm text-foreground-800">
                  你的知情程度
                  <select
                    name="credibility"
                    required
                    className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="亲历者">亲历者 / 在场者</option>
                    <option value="内部人士">内部相关人士</option>
                    <option value="知情人士">知情人士</option>
                    <option value="圈内转载">圈内转载线索</option>
                  </select>
                </label>
              </div>

              <label className="flex flex-col gap-1.5 text-sm text-foreground-800">
                事件标题
                <input
                  name="title"
                  type="text"
                  required
                  maxLength={40}
                  placeholder="用一句话概括你想爆料的重点"
                  className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>

              <label className="flex flex-col gap-1.5 text-sm text-foreground-800">
                事件描述
                <textarea
                  name="description"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="尽量说明时间、场合与可佐证的信息（限 500 字）"
                  className="resize-none rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>

              <label className="flex flex-col gap-1.5 text-sm text-foreground-800">
                联系邮箱（选填，用于核实与反馈）
                <input
                  name="email"
                  type="email"
                  placeholder="可选填，如希望收到跟进通知"
                  className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <label className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
                <input type="checkbox" name="agree" required className="mt-0.5 accent-primary-500" />
                <span>
                  我已阅读并同意投稿须知：仅提交真实可核实信息，不对他人进行不实指控，
                  不包含他人隐私，并接受平台对无法核实内容的标注处理。
                </span>
              </label>

              {feedback && (
                <div
                  role="status"
                  className={`rounded-lg px-3.5 py-2.5 text-sm ${
                    feedback.type === "success"
                      ? "bg-accent-100 text-accent-900"
                      : "bg-primary-100 text-primary-900"
                  }`}
                >
                  {feedback.text}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    匿名提交
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}